import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePaginatedList } from '../hooks/usePaginatedList';
import { Chip, EmptyState, InfiniteSentinel, SearchBar, Skeletons, listingPriceLabel, LISTING_TYPE_TONE, LISTING_STATUS_TONE } from '../components/kit';
import FilterSheet, { FilterButton, loadFilters, saveFilters } from '../components/FilterSheet';
import NotifBell from '../components/NotifBell';
import * as listingsApi from '../api/listings';

// Filter sections mirror the admin portal ListingsPage filters (Type / Status /
// Visibility). Others' listings only ever show ACTIVE / UNDER_OFFER
// (server-enforced), so that tab offers a reduced status set and no visibility.
// Selections persist across sessions.
const FILTER_STORE = 'cid.filters.listings';
const TYPE_SECTION = {
  key: 'listing_type',
  label: 'Type',
  options: [{ value: 'SUBSALE', label: 'Subsale' }, { value: 'RENT', label: 'Rent' }, { value: 'PROJECT', label: 'Project' }, { value: 'AUCTION', label: 'Auction' }],
};
const STATUS_MINE = ['DRAFT', 'ACTIVE', 'UNDER_OFFER', 'SOLD', 'RENTED', 'EXPIRED', 'WITHDRAWN'];
const STATUS_OTHERS = ['ACTIVE', 'UNDER_OFFER'];
const statusOption = (s) => ({ value: s, label: s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') });
const sectionsFor = (scope) => [
  TYPE_SECTION,
  { key: 'status', label: 'Status', options: (scope === 'mine' ? STATUS_MINE : STATUS_OTHERS).map(statusOption) },
  ...(scope === 'mine'
    ? [{ key: 'visibility', label: 'Visibility', options: [{ value: 'INTERNAL', label: 'Internal' }, { value: 'PUBLIC', label: 'Public' }] }]
    : []),
];

// A chip laid over a photograph cannot use the standard 16%-alpha tone — it
// vanishes against a bright sky. Same foreground colour, opaque plate behind it.
const onPhoto = { background: 'rgba(255,255,255,.93)', boxShadow: '0 1px 4px rgba(8,14,24,.35)' };

// Price psf is the number agents actually compare properties on, and it is the one
// figure the API never sends because it is pure arithmetic. Meaningless for a
// rental or an unpriced project, so those return null and the slot goes unrendered.
const pricePsf = (l) => {
  if (l.listing_type === 'RENT' || l.listing_type === 'PROJECT') return null;
  const area = Number(l.built_up_sqft) || Number(l.land_area_sqft) || 0;
  const price = Number(l.asking_price_rm) || 0;
  if (!area || !price) return null;
  return `RM ${Math.round(price / area).toLocaleString('en-US')} psf`;
};

// The address as an agent would say it out loud: street, then the town, skipping
// whichever parts this listing happens not to have.
const placeLine = (l) => [l.property_address, l.area, l.state].filter(Boolean).join(' · ');

const Feature = ({ icon, children }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 500, color: 'var(--text)', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 9px' }}>
    <span style={{ display: 'flex', color: 'var(--muted)', flex: 'none' }}>{icon}</span>
    {children}
  </span>
);

const ico = (d, extra) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
    {extra}
  </svg>
);
const BED = ico('M3 18v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5M3 18h18M6 11V8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v3');
const BATH = ico('M4 12h16v3a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4z', <path d="M7 12V6a2 2 0 0 1 4 0" />);
const CAR = ico('M5 16v-4l2-5h10l2 5v4', <><circle cx="8" cy="16" r="1.6" /><circle cx="16" cy="16" r="1.6" /></>);
const AREA = ico('M4 4h16v16H4z', <path d="M8 4v16M4 8h16" />);
const CAMERA = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round">
    <path d="M4 8h3l2-2h6l2 2h3v11H4z" /><circle cx="12" cy="13" r="3.2" />
  </svg>
);

export default function ListingsPage() {
  const nav = useNavigate();
  // Which tab you are on lives in the URL, not in component state. Opening a
  // listing and coming back re-mounts this page, and local state would have reset
  // to "My listings" — so an agent browsing co-broke stock lost their place on
  // every single back press. `?tab=others` survives that, and makes the tab
  // linkable and restorable.
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = searchParams.get('tab') === 'others' ? 'others' : 'mine';
  const [filters, setFilters] = useState(() => loadFilters(FILTER_STORE));
  const [sheetOpen, setSheetOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Only filters valid for the current scope count / apply (others: no visibility,
  // status restricted to ACTIVE/UNDER_OFFER).
  const effective = useMemo(() => {
    const arr = (k) => (Array.isArray(filters[k]) ? filters[k] : filters[k] ? [filters[k]] : []);
    const statuses = scope === 'mine' ? arr('status') : arr('status').filter((x) => STATUS_OTHERS.includes(x));
    return {
      listing_type: arr('listing_type').join(',') || undefined,
      status: statuses.join(',') || undefined,
      visibility: scope === 'mine' ? arr('visibility').join(',') || undefined : undefined,
    };
  }, [filters, scope]);
  const activeCount = Object.values(effective).filter(Boolean).length;

  const params = useMemo(
    () => ({ ...effective, search: search || undefined, scope }),
    [effective, search, scope]
  );
  // paginated list — sends { page, limit, listing_type, status, visibility, search, scope }
  const list = usePaginatedList(listingsApi.listListings, { limit: 10, params });

  const applyFilters = (v) => { setFilters(v); saveFilters(FILTER_STORE, v); };
  // replace, not push: a tab is a filter on one screen, not a place you travelled
  // to. Pushing would mean five taps back and forth leaves five entries to unwind
  // before back finally exits the listings page.
  const switchScope = (s) => setSearchParams(s === 'others' ? { tab: 'others' } : {}, { replace: true });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 16px 10px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 17, fontWeight: 600 }}>Listings</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>subsale &amp; rent</div>
          <div style={{ flex: 1 }} />
          <FilterButton count={activeCount} onClick={() => setSheetOpen(true)} />
          <NotifBell />
        </div>
        {/* My / Others tab switch */}
        <div style={{ display: 'flex', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: 3 }}>
          {[['mine', 'My listings'], ['others', "Others' listings"]].map(([k, label]) => {
            const on = scope === k;
            return (
              <div key={k} onClick={() => switchScope(k)} style={{ flex: 1, textAlign: 'center', fontSize: 13, fontWeight: 600, padding: '7px 0', borderRadius: 9, cursor: 'pointer', background: on ? 'var(--primary)' : 'transparent', color: on ? '#fff' : 'var(--muted)' }}>
                {label}
              </div>
            );
          })}
        </div>
        <SearchBar onSearch={setSearch} placeholder="Search ref, title or address…" />
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {list.loading ? (
          <Skeletons n={3} height={300} />
        ) : list.empty ? (
          <EmptyState
            title="No listings here"
            sub={scope === 'mine' ? 'Nothing matches this filter. Tap ＋ to create a listing.' : "No other agents' active listings match this filter."}
          />
        ) : (
          <>
            {list.items.map((l, idx) => {
              const psf = pricePsf(l);
              return (
                // Photo-first card: a 16:9 cover carrying the title and status over a
                // scrim, then the numbers. Fewer listings fit per screen than the old
                // thumbnail row, which is the trade — an agent browsing stock they
                // don't own is choosing on the property, not scanning for a ref.
                <div data-tour={idx === 0 ? 'listings-card' : undefined} key={l.id} onClick={() => nav(`/listings/${l.id}`)} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', cursor: 'pointer' }}>
                  <div style={{ position: 'relative', height: 168, background: 'repeating-linear-gradient(45deg,var(--surface2),var(--surface2) 7px,var(--chip) 7px,var(--chip) 14px)' }}>
                    {l.cover_image_url && (
                      <img src={l.cover_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    )}
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(8,14,24,.80) 0%,rgba(8,14,24,.30) 42%,rgba(8,14,24,0) 68%)' }} />
                    <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 5 }}>
                      <Chip label={l.listing_type} tone={LISTING_TYPE_TONE[l.listing_type]} style={onPhoto} />
                      <Chip label={String(l.status || '').replace(/_/g, ' ')} tone={LISTING_STATUS_TONE[l.status]} style={onPhoto} />
                    </div>
                    {l.image_count > 0 && (
                      <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: '#fff', background: 'rgba(8,14,24,.55)', borderRadius: 999, padding: '3px 9px' }}>
                        {CAMERA}{l.image_count}
                      </div>
                    )}
                    <div style={{ position: 'absolute', left: 13, right: 13, bottom: 11, color: '#fff' }}>
                      <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.25, letterSpacing: '-.01em' }}>{l.property_title}</div>
                      <div style={{ fontSize: 11.5, opacity: .85, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{placeLine(l)}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 9, padding: '13px 14px 0' }}>
                    <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.025em', lineHeight: 1 }}>{listingPriceLabel(l)}</span>
                    {psf && <span style={{ fontSize: 11.5, color: 'var(--muted)', paddingBottom: 1 }}>{psf}</span>}
                    <span style={{ flex: 1 }} />
                    {scope === 'others' ? (
                      // "CID HQ" on its own reads as a mystery badge — say what it means.
                      // Without a name there is nothing to say, and a bare "Listed by"
                      // reads as a rendering fault, so the whole line goes.
                      l.agent_name ? (
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--muted)', paddingBottom: 1, textAlign: 'right' }}>
                          Listed by {l.agent_name}{l.is_hq_listing ? ' (head office)' : ''}
                        </span>
                      ) : null
                    ) : (
                      <Chip label={l.visibility} tone={l.visibility === 'PUBLIC' ? 'ok' : 'brand'} style={{ marginBottom: 1 }} />
                    )}
                  </div>

                  {/* A PROJECT sells through units, so unit availability is its spec row. */}
                  <div style={{ display: 'flex', gap: 7, padding: '11px 14px 0', flexWrap: 'wrap' }}>
                    {l.listing_type === 'PROJECT' && l.project_stats ? (
                      <Feature icon={AREA}>{l.project_stats.available_units} of {l.project_stats.total_units} units available</Feature>
                    ) : (
                      <>
                        {l.bedrooms != null && <Feature icon={BED}>{l.bedrooms}</Feature>}
                        {l.bathrooms != null && <Feature icon={BATH}>{l.bathrooms}</Feature>}
                        {l.car_parks != null && <Feature icon={CAR}>{l.car_parks}</Feature>}
                        {(l.built_up_sqft || l.land_area_sqft) && (
                          <Feature icon={AREA}>{Number(l.built_up_sqft || l.land_area_sqft).toLocaleString('en-US')} sqft</Feature>
                        )}
                      </>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, padding: '9px 14px', borderTop: '1px solid var(--border)', background: 'var(--surface2)', fontSize: 11.5, color: 'var(--muted)' }}>
                    <span style={{ fontFamily: 'ui-monospace,monospace', flex: 'none' }}>{l.listing_ref}</span>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[l.property_type, l.property_tenure && l.property_tenure.toLowerCase()].filter(Boolean).map((s) => `· ${s}`).join(' ')}
                    </span>
                  </div>
                </div>
              );
            })}
            <InfiniteSentinel hasMore={list.hasMore} loading={list.loadingMore} onReach={list.loadMore} />
          </>
        )}
        <div style={{ height: 66 }} />
      </div>

      <div data-tour="listings-fab" onClick={() => nav('/listings/new')} style={{ position: 'absolute', right: 18, bottom: 18, width: 54, height: 54, borderRadius: 50, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, cursor: 'pointer', boxShadow: '0 8px 20px color-mix(in srgb,var(--primary) 45%,transparent)', zIndex: 5 }}>＋</div>

      <FilterSheet open={sheetOpen} title="Filter listings" sections={sectionsFor(scope)} value={filters} onApply={applyFilters} onClose={() => setSheetOpen(false)} />
    </div>
  );
}
